// Which embedded document previews the widget wraps, and the document URL behind each. The shapes
// come from what SchoolBlocks renders (react-website NormalizedFileBlock.tsx): a Drive preview,
// and Google's viewer wrapping any other URL. SharePoint embedview is not wrapped yet.
import { isMobileViewport } from "../viewer/placement";
import { hasTemporaryAuth, looksLikeDocumentUrl } from "./links";
import {
  embedLinkPolicy,
  iframeAutoOpenInitial,
  smallIframeGuard,
} from "./site-options";

const DRIVE_PREVIEW =
  /^https:\/\/(?:docs|drive)\.google\.com\/file\/d\/([\w-]+)\/preview\b/;

/** Previews smaller than this are cards, not readers (isTinyAutoOpenIframeHost, docbox.js:36094). */
export const MIN_PREVIEW = { width: 560, height: 320 };

/**
 * Whether a painted preview of this size opens the accessible view by itself. Otherwise it is a
 * card that opens the viewer on a click. The legacy order (shouldAutoOpenInitialIframeDoc,
 * docbox.js:36330-36378): DOCBOX_EMBED_LINK_POLICY "mixed_strict", then a phone or tablet
 * viewport, then the small-card guard, then DOCBOX_IFRAME_AUTO_OPEN_INITIAL === false. Each
 * preview the widget wraps is a Google document signal (isGoogleDriveDocSignalUrl,
 * docbox.js:36066), so the checks after that one in legacy always open.
 */
export function opensByItself(
  box: { width: number; height: number },
  win: Window = window,
): boolean {
  if (embedLinkPolicy(win) === "mixed_strict") return false;
  if (isMobileViewport(win)) return false;
  const small =
    box.width < MIN_PREVIEW.width || box.height < MIN_PREVIEW.height;
  if (small && smallIframeGuard(win)) return false;
  return iframeAutoOpenInitial(win) !== false;
}

/** The document URL to parse for a preview iframe's src, or null when it is not a preview. */
export function documentUrlFromPreviewSrc(src: string): string | null {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || hasTemporaryAuth(src)) return null;

  const drive = DRIVE_PREVIEW.exec(url.href);
  if (drive) return `https://drive.google.com/file/d/${drive[1]}/view`;

  // Google's viewer: the document is the url parameter (decoded by searchParams).
  if (url.hostname === "docs.google.com" && url.pathname === "/viewer") {
    const inner = url.searchParams.get("url") ?? "";
    if (!inner.startsWith("https://")) return null;
    if (!isWrappedDocument(inner)) return null;
    return inner;
  }
  return null;
}

/**
 * Whether the URL inside Google's viewer is a document (isGoogleViewerWrappedDocumentUrl,
 * docbox.js:35247): a PDF, a SharePoint or OneDrive link, a document link the widget opens, a
 * Google Workspace file, or an Office, ODF, RTF, text or CSV file (looksLikeDocumentUrl,
 * docbox.js:35607-35670). Legacy leaves any other wrapped URL to the host.
 */
function isWrappedDocument(href: string): boolean {
  if (/\.pdf(?:$|[?#])/i.test(href)) return true;
  if (looksLikeDocumentUrl(href)) return true;
  if (
    /docs\.google\.com\/(?:file|document|spreadsheets|presentation|drawings)\/d\//.test(
      href,
    )
  ) {
    return true;
  }
  if (/sharepoint\.com|onedrive\.live\.com|1drv\.ms/i.test(href)) return true;
  return /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv)(?:[?#]|$)/i.test(href);
}
